import { GetItemsProps, getDate, toISOString } from "medere-ui-framework";
import { axiosInstance } from "../../App";
import { CompleteBudgetDetailDTO } from "../../dto/budget/BudgetDetailDTO";
import { adaptBudgetDTO, BudgetDTO } from "../../dto/budget/BudgetDTO";
import { fromDateToString } from "../../utils/utils";
import { BillHospOrdersSummaryDTO } from "../../dto/budget/BillHospOrdersSummaryDTO";

/**
 * Valoriza una práctica de la facturación de internaciones. El backend decide la modalidad de contrato:
 * si viene de una cirugía (surgeryId), internación si fue durante una internación y si no ambulatoria;
 * si no, internación cuando la factura tiene internación (withHospitalization) y ambulatoria cuando no.
 */
export function getNomenclatorValueForHospitalization(nomenclator: number, date: Date | undefined, healthEntityPlan: number | undefined, billingEntity: number | null | undefined, withHospitalization: boolean, surgeryId?: number | null) {
  return axiosInstance.get('/api/secured/billhospitalization/getNomenclatorValue', {
    params: {
      nomenclator,
      UTCDate: date?.getTime(),
      healthEntityPlan,
      withHospitalization,
      ...(billingEntity && billingEntity > 0 ? { billingEntity } : {}),
      ...(surgeryId ? { surgeryId } : {})
    }
  }).then((response) => {
    return response.data;
  }).catch(() => {
    return null;
  });
}

export function exportBillHospitalizationPDF(id: number, viewMode: number, detailLevel: number) {
  return axiosInstance
    .get('/api/secured/billhospitalization/exportBillHospitalizationPDF', {
      params: { budgetId: id, viewMode, detailLevel },
      responseType: 'blob',
    })
    .then((response) => {
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `facturacion_internacion_${id}.pdf`;

      document.body.appendChild(a);
      a.click();

      setTimeout(() => {
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }, 0);
    })
    .catch((error) => {
      console.error('Error al descargar el PDF:', error);
      throw error;
    });
}

export function getBillHospitalizationViewModes({ filterValue, offSet, pageSize }: GetItemsProps) {
  return axiosInstance.get('/api/secured/billhospitalization/getBillHospitalizationViewModes', {
    params: { offSet, pageSize, inputText: filterValue ?? '' }
  }).then((response) => {
    return response.data;
  }).catch((error) => {
    console.error(error);
    throw error;
  });
}

export function getBillHospitalizationDetailLevels({ filterValue, offSet, pageSize }: GetItemsProps) {
  return axiosInstance.get('/api/secured/billhospitalization/getBillHospitalizationDetailLevels', {
    params: { offSet, pageSize, inputText: filterValue ?? '' }
  }).then((response) => {
    return response.data;
  }).catch((error) => {
    console.error(error);
    throw error;
  });
}

export function getProfessionalActivitiesWithContract({ filterValue, offSet, pageSize, params }: GetItemsProps) {
  const serviceDate = params.serviceDate ? getDate(params.serviceDate) : undefined;
  return axiosInstance.get('/api/secured/billhospitalization/getProfessionalActivitiesWithContract', {
      params: {
          offSet,
          pageSize,
          inputText: filterValue ?? '',
          orderByNomenclatorType: params.orderByNomenclatorType,
          healthEntity: params.healthEntity,
          serviceDate: serviceDate
            ? `${serviceDate.getFullYear()}-${String(serviceDate.getMonth() + 1).padStart(2, '0')}-${String(serviceDate.getDate()).padStart(2, '0')}`
            : undefined
      }
  }).then((response) => {
      return response.data;
  }).catch((error) => {
      console.error(error);
      throw error;
  });
}

export function getNomenclatorsForHospitalizationDays({ filterValue, offSet, pageSize, params }: GetItemsProps) {
  const serviceDate = params.serviceDate ? getDate(params.serviceDate) : undefined;
  return axiosInstance.get('/api/secured/billhospitalization/getNomenclatorsForHospitalizationDays', {
      params: {
          offSet,
          pageSize,
          inputText: filterValue ?? '',
          orderByNomenclatorType: params.orderByNomenclatorType,
          hospitalizationId: params.hospitalizationId,
          healthEntity: params.healthEntity,
          serviceDate: serviceDate
            ? `${serviceDate.getFullYear()}-${String(serviceDate.getMonth() + 1).padStart(2, '0')}-${String(serviceDate.getDate()).padStart(2, '0')}`
            : undefined
      }
  }).then((response) => {
      return response.data;
  }).catch((error) => {
      console.error(error);
      throw error;
  });
}

export function getAllBillablePatients({ filterValue, offSet, pageSize, params }: GetItemsProps) {
  return axiosInstance.get('/api/secured/billhospitalization/getAllBillablePatients', {
    params: {
      offSet,
      pageSize,
      inputText: filterValue ?? '',
      fromDate: fromDateToString(getDate(params.fromDate)),
      toDate: fromDateToString(getDate(params.toDate)),
    }
  }).then((response) => {
    return response.data;
  }).catch((error) => {
    console.error(error);
    throw error;
  });
}

export function getNutritionalSupport({ filterValue, offSet, pageSize }: GetItemsProps) {
  return axiosInstance.get('/api/secured/billhospitalization/getNutritionalSupport', {
    params: { offSet, pageSize, inputText: filterValue ?? '' }
  }).then((response) => {
    return response.data;
  }).catch((error) => {
    console.error(error);
    throw error;
  });
}

export function getMedicines({ filterValue, offSet, pageSize }: GetItemsProps) {
  return axiosInstance.get('/api/secured/billhospitalization/getMedicines', {
    params: { offSet, pageSize, inputText: filterValue ?? '' }
  }).then((response) => {
    return response.data;
  }).catch((error) => {
    console.error(error);
    throw error;
  });
}

export function getDisposablesStockItemsForCombo({ filterValue, offSet, pageSize }: GetItemsProps) {
  return axiosInstance.get('/api/secured/billhospitalization/getDisposablesStockItemsForCombo', {
    params: { offSet, pageSize, inputText: filterValue ?? '' }
  }).then((response) => {
    return response.data;
  }).catch((error) => {
    console.error(error);
    throw error;
  });
}

export function getStockItemPresentationValue(id: number) {
  return axiosInstance.get('/api/secured/billhospitalization/getStockItemPresentationValue', {
    params: { stockitempresentation: id }
  }).then((response) => {
    return response.data;
  }).catch(() => {
    return null;
  });
}

export function updateBudgetPrices(budget: BudgetDTO): Promise<BudgetDTO> {
  return axiosInstance.post('/api/secured/billhospitalization/updateBudgetPrices', budget)
    .then((response) => response.data)
    .catch((error) => {
      console.error(error);
      throw error;
    });
}

export function saveBudget(budget: BudgetDTO) {
  return axiosInstance.post('/api/secured/billhospitalization/saveBudget', {
    ...budget,
    date: toISOString(getDate(budget.date)),
    laststatechange: toISOString(getDate(budget.laststatechange))
  }).then(res => res);
}

export function getModalities({ filterValue, offSet, pageSize }: GetItemsProps) {
  return axiosInstance.get('/api/secured/billhospitalization/getModalities', {
    params: {
      offSet,
      pageSize,
      inputText: filterValue ?? ''
    }
  }).then((response) => {
    return response.data;
  }).catch((error) => {
    console.error(error);
    throw error;
  });
}

export function getBillingEntities({ filterValue, offSet, pageSize }: GetItemsProps) {
  return axiosInstance.get('/api/secured/billhospitalization/getBillingEntities', {
    params: {
      offSet,
      pageSize,
      inputText: filterValue ?? '',
    }
  }).then((response) => {
    return response.data;
  }).catch((error) => {
    console.error(error);
    throw error;
  });
}

export function generateBenefitOrderFromBudget(budget: BudgetDTO) {
  return axiosInstance.post('/api/secured/billhospitalization/generateBenefitOrderFromBudget', {
    ...budget,
    date: toISOString(getDate(budget.date)),
    laststatechange: toISOString(getDate(budget.laststatechange))
  }).then(res => res);
}

/**
 * PDF de una orden de prestación de la factura sanatorial, con los mismos montos que el resumen de órdenes.
 * detailLevel: "Valores a mostrar", como en el PDF de la factura (getBillHospitalizationDetailLevels).
 */
export async function downloadBenefitOrderPdf(budgetId: number, benefitOrderId: number, detailLevel: number): Promise<void> {
  const response = await axiosInstance.get('/api/secured/billhospitalization/exportBenefitOrderPDF', {
    params: { budgetId, benefitOrderId, detailLevel },
    responseType: 'blob',
  });
  const url = URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = `orden-de-prestacion-${benefitOrderId}.pdf`;
  a.click();
  URL.revokeObjectURL(url);
}

/**
 * PDF de lo prefacturado de la factura sanatorial, sin dividir entre paciente y obra social: sale de las órdenes
 * generadas, no de la factura guardada. detailLevel: "Valores a mostrar".
 */
export async function exportPreinvoicedBillPDF(budgetId: number, detailLevel: number): Promise<void> {
  const response = await axiosInstance.get('/api/secured/billhospitalization/exportPreinvoicedBillPDF', {
    params: { budgetId, detailLevel },
    responseType: 'blob',
  });
  const url = URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = `prefacturacion_internacion_${budgetId}.pdf`;
  a.click();
  URL.revokeObjectURL(url);
}

/** Órdenes de prestación de la factura sanatorial, con los ítems agrupados por tipo y los subtotales. */
export function getBenefitOrdersByBudget(budgetId: number): Promise<BillHospOrdersSummaryDTO> {
  return axiosInstance.get('/api/secured/billhospitalization/getBenefitOrdersByBudget', {
    params: { budgetId }
  }).then(res => res.data);
}

export function getAllBudgets(offset: number, pageSize: number, params: any): Promise<BudgetDTO[]> {
  return axiosInstance.post('/api/secured/billhospitalization/getAllBudgets', {
    offSet: offset,
    pageSize,
    filters: params.filterDTO,
    sortingFieldInfoList: params.multiSortMeta
  }, {
    params: {
      filterAddressee: params.filterAddressee,
      filterHealthEntity: params.filterHealthEntity,
      filterBudgetId: params.filterBudgetId,
      UTCfilterDate: params.filterDate?.getTime(),
      filterDetail: params.filterDetail,
      filterState: params.filterState,
      filterHospitalization: params.filterHospitalization
    }
  }).then((response) => response.data)
    .catch((error) => {
      console.error(error);
    });
}

export function getBudgetComplete(id: number): Promise<BudgetDTO> {
  return axiosInstance.get('/api/secured/billhospitalization/getBudgetComplete', {
    params: { budgetId: id }
  }).then((response) => adaptBudgetDTO(response.data))
    .catch((error) => {
      console.error(error);
      throw error;
    });
}

export function getBudgetStates({ filterValue, offSet, pageSize }: GetItemsProps) {
  return axiosInstance.get('/api/secured/billhospitalization/getBudgetStates', {
    params: { offSet, pageSize, inputText: filterValue ?? '' }
  }).then((response) => response.data)
    .catch((error) => {
      console.error(error);
      throw error;
    });
}

export function cancelBudget(budget: number) {
  return axiosInstance.put('/api/secured/billhospitalization/cancelBudget', {}, { params: { budget } })
    .then(res => res.data);
}

export function getBudgetDetailByPatient(patientId: number, fromDate: Date, toDate: Date, billingEntity?: number | null): Promise<CompleteBudgetDetailDTO[]> {
  return axiosInstance
    .get('/api/secured/billhospitalization/getBudgetDetailByPatient', {
      params: {
        patientId,
        fromDate: fromDateToString(getDate(fromDate)),
        toDate: fromDateToString(getDate(toDate)),
        ...(billingEntity && billingEntity > 0 ? { billingEntity } : {}),
      },
    })
    .then((response) => response.data)
    .catch((error) => {
      console.error(error);
      throw error;
    });
}
